// FPPlayerCard.js
// Franchise Player — player card: this week's salary, projection, matchup and
// Vegas line, plus the season game log (from our stored box scores).

import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { FP_ACCENT } from "./fp-theme";
import { formatMoney, injuryInfo } from "./fp-config";
import { TeamLogo, PosBadge, MatchupTag, matchupInfo, vegasText, fmtPts, valueOf, fmtOdds, GameLine, gameState } from "./fp-stats";

function Stat({ label, value, accent }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 10px", minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,0.4)" }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 0.5, color: accent || "white" }}>{value}</div>
    </div>
  );
}

// Columns for the game log, by position.
function logColumns(pos) {
  if (pos === "DEF") return [["Sack", (g) => g.sacks || 0], ["INT", (g) => g.int || 0], ["FR", (g) => g.fumRec || 0], ["TD", (g) => g.td || 0], ["PA", (g) => g.ptsAllowed ?? 0]];
  if (pos === "QB") return [
    ["Pass", (g) => `${g.passCmp || 0}/${g.passAtt || 0}`], ["Yds", (g) => g.passYd || 0], ["TD", (g) => g.passTD || 0], ["INT", (g) => g.int || 0],
    ["Rush", (g) => `${g.rushAtt || 0}-${g.rushYd || 0}`], ["RTD", (g) => g.rushTD || 0],
  ];
  if (pos === "RB") return [
    ["Car", (g) => g.rushAtt || 0], ["RuYd", (g) => g.rushYd || 0], ["RuTD", (g) => g.rushTD || 0],
    ["Tgt", (g) => g.tgt || 0], ["Rec", (g) => g.rec || 0], ["ReYd", (g) => g.recYd || 0], ["ReTD", (g) => g.recTD || 0],
  ];
  return [["Tgt", (g) => g.tgt || 0], ["Rec", (g) => g.rec || 0], ["Yds", (g) => g.recYd || 0], ["TD", (g) => g.recTD || 0], ["Rush", (g) => `${g.rushAtt || 0}-${g.rushYd || 0}`]];
}

export default function FPPlayerCard({ playerId, player, week, season, games, proj, odds, dvp, pool, onClose, onAdd, addLabel, captainMult, sbGame, liveStat }) {
  const [log, setLog] = useState(null);
  const team = player?.team;
  const pos = player?.pos;

  useEffect(() => {
    if (!team) return;
    let cancelled = false;
    getDoc(doc(db, "fp_logs", `${season}_${team}`))
      .then((snap) => { if (!cancelled) setLog(snap.exists() ? snap.data().players?.[playerId] || { games: {} } : { games: {} }); })
      .catch(() => { if (!cancelled) setLog({ games: {} }); });
    return () => { cancelled = true; };
  }, [playerId, team, season]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = useMemo(() => Object.entries(log?.games || {})
    .map(([wk, g]) => ({ week: parseInt(wk, 10), ...g }))
    .sort((a, b) => b.week - a.week), [log]);

  if (!player) return null;
  const g = games?.[team];
  const opp = g?.opp;
  const p = proj?.players?.[playerId];
  const m = opp ? matchupInfo(dvp, opp, pos) : null;
  const val = valueOf(p?.pts, player.fd);
  const tdOdds = odds?.anytd?.[playerId];
  const injury = pool?.[playerId]?.injury;
  const inj = injury ? injuryInfo(injury.designation, injury.returnDate) : null;
  const pts = rows.map((r) => r.fpts || 0);
  const avg = pts.length ? pts.reduce((s, x) => s + x, 0) / pts.length : null;
  const last3 = pts.length ? pts.slice(0, 3).reduce((s, x) => s + x, 0) / Math.min(3, pts.length) : null;
  const cols = logColumns(pos);

  return (
    <div role="dialog" aria-modal="true" aria-label={`${player.name} player card`} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(3,8,18,0.72)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 12px", overflowY: "auto" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, padding: 0, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "16px 18px", background: "linear-gradient(135deg, rgba(20,184,166,0.15), transparent)", borderBottom: "1px solid var(--border)", display: "flex", gap: 14, alignItems: "center" }}>
          <TeamLogo team={team} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: 1, lineHeight: 1 }}>{player.name}</span>
              <PosBadge pos={pos} />
              {inj && <span title={injury.note || injury.designation} style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, color: inj.short === "Q" ? "#FBBF24" : "#F87171", border: "1px solid currentColor" }}>{injury.designation}</span>}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {g && gameState(sbGame, g.kickoff).state !== "upcoming"
                ? <GameLine team={team} opp={opp} home={g.home} kickoff={g.kickoff} sbGame={sbGame} />
                : <>{team}{g ? <>{g.home ? " vs" : " @"} <TeamLogo team={opp} size={16} /> {opp} · {new Date(g.kickoff).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}</> : " · bye"}</>}
            </div>
            {liveStat && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 3 }}>{liveStat}</div>}
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" aria-label="Close">✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {/* This week */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 8, marginBottom: 10 }}>
            <Stat label={`WEEK ${week} SALARY`} value={player.fd ? formatMoney(player.fd) : "—"} />
            <Stat label="PROJECTED" value={p ? fmtPts(p.pts) : "—"} accent={captainMult ? null : FP_ACCENT} />
            {captainMult && <Stat label={`AS FRANCHISE (${captainMult}x)`} value={p ? fmtPts(p.pts * captainMult) : "—"} accent={FP_ACCENT} />}
            <Stat label="PTS / $1K" value={val ? val.toFixed(2) : "—"} />
            <Stat label="SEASON AVG" value={avg != null ? fmtPts(avg) : "—"} />
            <Stat label="LAST 3 AVG" value={last3 != null ? fmtPts(last3) : "—"} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 14 }}>
            {m && <MatchupTag info={m} />}
            {m && <span>{m.title}</span>}
          </div>
          {(vegasText(odds, team) || tdOdds) && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 14, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {vegasText(odds, team) && <span>🎲 {vegasText(odds, team)}</span>}
              {tdOdds && <span>Anytime TD {fmtOdds(tdOdds)}</span>}
            </div>
          )}
          {p?.line && pos !== "DEF" && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 14 }}>
              Projection: {[
                p.line.passYd && `${p.line.passYd} pass yds`, p.line.passTD && `${p.line.passTD} pass TD`,
                p.line.rushYd && `${p.line.rushYd} rush yds`, p.line.rushTD && `${p.line.rushTD} rush TD`,
                p.line.rec && `${p.line.rec} rec`, p.line.recYd && `${p.line.recYd} rec yds`, p.line.recTD && `${p.line.recTD} rec TD`,
              ].filter(Boolean).join(" · ")}
            </div>
          )}

          {/* Game log */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "rgba(255,255,255,0.4)", marginBottom: 6 }}>
            GAME LOG{captainMult && <span style={{ letterSpacing: 0, fontWeight: 500, textTransform: "none" }}> · regular points, before the {captainMult}x</span>}
          </div>
          {log === null ? (
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>Loading...</p>
          ) : rows.length === 0 ? (
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>No games played yet this season.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "rgba(255,255,255,0.4)", textAlign: "right" }}>
                    <th style={{ ...th, textAlign: "left" }}>Wk</th>
                    <th style={{ ...th, textAlign: "left" }}>Opp</th>
                    <th style={th}>FPTS</th>
                    {cols.map(([h]) => <th key={h} style={th}>{h}</th>)}
                    {pos !== "DEF" && <th style={th}>Snap%</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.week} style={{ borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>
                      <td style={{ ...td, textAlign: "left" }}>{r.week}</td>
                      <td style={{ ...td, textAlign: "left", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{r.home ? "vs" : "@"} <TeamLogo team={r.opp} size={14} /> {r.opp}</span>
                      </td>
                      <td style={{ ...td, fontWeight: 800, color: FP_ACCENT }}>{fmtPts(r.fpts)}</td>
                      {cols.map(([h, f]) => <td key={h} style={td}>{f(r)}</td>)}
                      {pos !== "DEF" && <td style={td}>{r.snap != null ? `${r.snap}%` : "—"}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {onAdd && (
            <button onClick={onAdd} className="btn btn-fp" style={{ marginTop: 16, width: "100%", justifyContent: "center" }}>
              {addLabel || "Add to lineup"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const th = { padding: "4px 6px", fontWeight: 600, whiteSpace: "nowrap" };
const td = { padding: "6px 6px", color: "rgba(255,255,255,0.8)" };
